import type { ApiResponse } from "@wedding/shared";

/**
 * Guest web API client — always uses relative paths.
 * In production, wedding.mertbal.com proxies /api/* to the Worker.
 * In dev, Vite proxy forwards /api/* to localhost:8787.
 */

function buildBase(slug: string): string {
  return `/api/w/${slug}`;
}

async function request<T>(
  base: string,
  path: string,
  options?: RequestInit
): Promise<ApiResponse<T>> {
  const url = path && path !== "/" ? `${base}${path}` : base;
  const res = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...options?.headers },
    ...options,
  });

  const text = await res.text();
  let json: ApiResponse<T>;
  try {
    json = JSON.parse(text) as ApiResponse<T>;
  } catch {
    throw new Error(`Sunucu hatası: ${res.status} ${res.statusText}`);
  }

  if (!res.ok) {
    // 401 (session expired) or 403 Banned → broadcast so auth-context can clear state
    if (res.status === 401 || (res.status === 403 && json.error === "Banned")) {
      window.dispatchEvent(new CustomEvent("guest:unauthenticated", { detail: { reason: json.error } }));
    }
    throw new Error(json.error ?? `Request failed: ${res.status}`);
  }

  return json;
}

/**
 * XHR send with upload-progress events (fetch() can't report upload progress).
 * Used for both the proxied media upload and the direct-to-R2 PUT so large
 * videos show a live progress bar instead of an ambiguous spinner.
 */
function xhrSend(opts: {
  url: string;
  method: string;
  body: Blob | FormData;
  headers?: Record<string, string>;
  withCredentials?: boolean;
  onProgress?: (pct: number) => void;
}): Promise<{ status: number; text: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(opts.method, opts.url);
    if (opts.withCredentials) xhr.withCredentials = true;
    for (const [k, v] of Object.entries(opts.headers ?? {})) xhr.setRequestHeader(k, v);
    if (opts.onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) opts.onProgress!(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText });
    xhr.onerror = () => reject(new Error("Ağ hatası — yükleme başarısız"));
    xhr.ontimeout = () => reject(new Error("Zaman aşımı"));
    xhr.send(opts.body);
  });
}

export const createApi = (slug: string) => {
  const base = buildBase(slug);
  return {
    get: <T>(path: string) => request<T>(base, path),
    post: <T>(path: string, body?: unknown) =>
      request<T>(base, path, {
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
      }),
    patch: <T>(path: string, body?: unknown) =>
      request<T>(base, path, {
        method: "PATCH",
        body: body ? JSON.stringify(body) : undefined,
      }),
    del: <T>(path: string) =>
      request<T>(base, path, { method: "DELETE" }),
    upload: async <T>(path: string, formData: FormData, onProgress?: (pct: number) => void) => {
      const uploadUrl = path && path !== "/" ? `${base}${path}` : base;
      const { status, text } = await xhrSend({
        url: uploadUrl, method: "POST", body: formData, withCredentials: true, onProgress,
      });
      let json: ApiResponse<T>;
      try {
        json = JSON.parse(text) as ApiResponse<T>;
      } catch {
        throw new Error(`Sunucu hatası: ${status}`);
      }
      if (status < 200 || status >= 300) {
        if (status === 401 || (status === 403 && json.error === "Banned")) {
          window.dispatchEvent(new CustomEvent("guest:unauthenticated", { detail: { reason: json.error } }));
        }
        throw new Error(json.error ?? `Request failed: ${status}`);
      }
      return json;
    },
    /**
     * Direct-to-R2 upload for large files (bypasses the Worker request-body
     * ceiling): ask for a presigned PUT URL, stream the file straight to R2,
     * then finalize. The server re-validates the stored bytes on /complete.
     */
    uploadDirect: async (file: File, kind: "image" | "video", onProgress?: (pct: number) => void) => {
      const init = await request<{ mediaId: string; key: string; uploadUrl: string }>(
        base,
        "/media/upload-url",
        { method: "POST", body: JSON.stringify({ filename: file.name, contentType: file.type, kind }) },
      );
      const { mediaId, key, uploadUrl } = init.data!;
      const { status } = await xhrSend({
        url: uploadUrl, method: "PUT", body: file,
        headers: { "Content-Type": file.type }, onProgress,
      });
      if (status < 200 || status >= 300) throw new Error(`R2 yükleme başarısız: ${status}`);
      await request(base, "/media/complete", {
        method: "POST",
        body: JSON.stringify({ mediaId, r2Key: key }),
      });
    },
  };
};

// Legacy default for backward compat during migration
export const api = createApi("default");

// ─── Activity Feed ──────────────────────────────────────────
export interface ActivityItem {
  type: "like" | "comment" | "upload" | "upload_pending";
  id: string;
  guest_id: string;
  guest_name: string;
  media_id: string;
  media_r2_key: string;
  media_kind: "image" | "video";
  content: string | null;
  created_at: string;
}

export interface ActivityResponse {
  data: ActivityItem[];
  hasMore: boolean;
}

export interface TopLikedItem {
  id: string;
  kind: "image" | "video";
  r2_key: string;
  like_count: number;
  uploader_name: string | null;
}

export async function getTopLiked(slug: string): Promise<TopLikedItem[]> {
  const base = buildBase(slug);
  const res = await fetch(`${base}/media/top-liked`, { credentials: "include" });
  if (!res.ok) throw new Error(`Failed to load top liked: ${res.status}`);
  const json = await res.json() as { ok: boolean; data: TopLikedItem[] };
  return json.data ?? [];
}

export async function getActivity(
  slug: string,
  filter: "all" | "reactions" | "uploads",
  cursor?: string
): Promise<ActivityResponse> {
  const base = buildBase(slug);
  const params = new URLSearchParams({ filter, limit: "50" });
  if (cursor) params.set("cursor", cursor);
  const res = await fetch(`${base}/activity?${params}`, { credentials: "include" });
  if (!res.ok) throw new Error(`Failed to load activity: ${res.status}`);
  const json = await res.json() as { ok: boolean; data: ActivityItem[]; hasMore: boolean };
  return { data: json.data ?? [], hasMore: json.hasMore ?? false };
}

// ─── Read Cursors ───────────────────────────────────────────
export async function markSeen(
  slug: string,
  scope: "gallery" | "activity",
  lastSeenAt: string
): Promise<void> {
  const base = buildBase(slug);
  await fetch(`${base}/auth/seen`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ scope, lastSeenAt }),
  });
}
